<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { API, HOST } from '@/utils/http'
import { Spinner } from '@/components/ui/spinner'

const props = defineProps({
  layer: {
    type: Object,
    required: true
  }
})

const store = useStore()
const route = useRoute()
const orgId = computed(() => route.params.org_id)
const layerCode = computed(() => props.layer?.uuid)
const selectedRange = computed(() => store.getters['layers/getSelectedRange'])

const radar = ref(null)
const isLoading = ref(true)
const errorMessage = ref('')
const expandedCategories = ref({})

const getStatusColor = (status) => {
  switch (status) {
    case 1: return 'text-green-500'
    case 0: return 'text-red-500'
    default: return 'text-gray-500'
  }
}

const groupedTracks = computed(() => {
  if (!radar.value || !radar.value.tracks) return {}

  const grouped = {}
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  const tracks = [...radar.value.tracks].sort((a, b) => new Date(b.last_update) - new Date(a.last_update))

  tracks.forEach(track => {
    const trackDate = new Date(track.last_update)
    trackDate.setHours(0, 0, 0, 0)

    let dateKey

    if (trackDate.getTime() === today.getTime()) {
      dateKey = 'Today'
    } else if (trackDate.getTime() === yesterday.getTime()) {
      dateKey = 'Yesterday'
    } else {
      dateKey = trackDate.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    }

    if (!grouped[dateKey]) {
      grouped[dateKey] = []
    }

    grouped[dateKey].push(track)
  })

  return grouped
})

const toggleCategoryExpansion = (date) => {
  expandedCategories.value[date] = !expandedCategories.value[date]
}

const getVisibleTracks = (date, tracks) => {
  if (expandedCategories.value[date]) {
    return tracks
  }
  return tracks.slice(0, 8)
}

const fetchResults = async () => {
  if (!layerCode.value || !orgId.value) return

  isLoading.value = true
  try {
    let url = `${HOST}/org/${orgId.value}/layers/${layerCode.value}/radar/result`

    const range = selectedRange.value
    if (range && range.length === 2) {
      url += `?startDate=${encodeURIComponent(range[0])}&endDate=${encodeURIComponent(range[1])}`
    }

    const response = await API({
      method: 'GET',
      url
    })

    radar.value = response.data
  } catch (e) {
    console.error('Error fetching radar data:', e)
    errorMessage.value = `Failed to load radar data: ${e.message || 'Unknown error'}`
  } finally {
    isLoading.value = false
  }
}

watch(selectedRange, () => {
  fetchResults()
}, { deep: true })

onMounted(() => {
  fetchResults()
})
</script>

<template>
  <div>
    <div v-if="isLoading" class="flex items-center gap-2 py-4">
      <Spinner class="w-4 h-4" />
      <span class="text-sm text-muted-foreground">Loading radar data...</span>
    </div>

    <div v-else-if="errorMessage" class="py-4">
      <p class="text-sm text-destructive">{{ errorMessage }}</p>
    </div>

    <div v-else-if="radar">
      <!-- Radar info -->
      <div class="flex items-center justify-between mb-3">
        <div class="flex items-center gap-2">
          <span class="font-medium">{{ radar.name }}</span>
          <span :class="getStatusColor(radar.radar_status)">&#9679;</span>
        </div>
        <span class="text-sm text-muted-foreground">{{ radar.target_count }} targets</span>
      </div>

      <!-- Grouped tracks list -->
      <div class="space-y-1">
        <div
          v-for="(tracks, date) in groupedTracks"
          :key="date"
          class="mb-4"
        >
          <div class="font-medium text-sm text-gray-700 mb-2 border-b pb-1 flex items-center">
            <span class="mr-2">&#128197;</span>
            <span>{{ date }}</span>
            <span class="text-xs ml-2 text-gray-500">({{ tracks.length }} tracks)</span>
          </div>
          <div
            v-for="track in getVisibleTracks(date, tracks)"
            :key="track.id"
            class="text-sm py-1 px-2 rounded hover:bg-gray-50"
          >
            <div class="flex items-center justify-between">
              <span>Track #{{ track.id }}</span>
              <span class="text-xs text-muted-foreground">{{ new Date(track.last_update).toLocaleTimeString() }}</span>
            </div>
          </div>
          <div v-if="tracks.length > 8" class="text-center mt-2">
            <button
              @click="toggleCategoryExpansion(date)"
              class="text-blue-500 text-sm hover:underline"
            >
              {{ expandedCategories[date] ? 'Show less' : `Show ${tracks.length - 8} more tracks` }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <p v-else class="text-sm text-muted-foreground py-4">No radar data available</p>
  </div>
</template>
